import {
  Avatar,
  Box,
  IconButton,
  Stack,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Drawer,
  Typography,
} from "@mui/material";
import { AccountCircle } from "@mui/icons-material";
import { useContext, useEffect, useState } from "react";
import MuiSidebarExtended from "./MuiSidebarExtended";
import {
  BookOpenIcon,
  QuestionMarkCircleIcon,
  FingerPrintIcon,
} from "@heroicons/react/24/outline";
import { SubscriptionManager } from "../../components/SubscriptionManager";

import sunIcon from "/src/assets/icons-regular-sun.svg";
import moonIcon from "/src/assets/icons-regular-moon.svg";

import LogoContainer from "/src/assets/Fight Less-Win More Icon 75px.svg"
import LogoContainerInvert from "/src/assets/Fight Less-Win More Icon 75px.svg"

import RegularIcon from "/src/assets/icons-regular.svg";
import RegularIcon5 from "/src/assets/icons-regular-5.svg";
import { useAppStateContext } from "../../state/AppContextProvider";
import NinjaIcon from "../../assets/chatbotninja.png";
import NinjaActiveIcon from "../../assets/icons-ninja-active.png";
import { FAQItem, GlossaryItem, OrchestrationType } from "../../common/models";
import { useNavigate } from "react-router-dom";
import { AccessContext } from "../../state/AccessProvider";
import { fill } from "lodash";
import { IconRegular4 } from "../../assets/icons-regular-4-comp";

// Define DeceptionItem type
interface DeceptionItem {
  id: string;
  technique: string;
  description: string;
  category?: string;
}
import lightIcon from "/src/assets/Fight Less-Win More Icon 720px_2.svg";
import invertedLightIcon from "/src/assets/Fight Less-Win More Icon 720px_2.svg";

// Icon visibility toggles
const isAdminScreen = window.location.hash.startsWith("#/admin");
const SHOW_HOME_ICON = false;
const SHOW_NAV1_ICON = true; // Show the Glossary icon
const SHOW_NAV2_ICON = true; // Show the FAQ icon
const SHOW_NAV3_ICON = true; // Show the Deception icon
const SHOW_NAV4_ICON = true; // Show the Ninja icon

const SHOW_THEME_ICON = false;
const SHOW_AVATAR = true; // Example: always show avatar

let timeout: any;
interface MuiSidebarProps {
  profilePhoto: string | null;
  handleMenu: (event: React.MouseEvent<HTMLElement>) => void;
  anchorEl: null | HTMLElement;
  handleClose: () => void;
  onSidebarInsert: (text: string, orchestrationType: OrchestrationType) => void | Promise<void>;
}

export const MuiSidebar = ({
  profilePhoto,
  handleMenu,
  anchorEl,
  handleClose,
  onSidebarInsert,
}: MuiSidebarProps): JSX.Element => {
  const [hoverIcon, setHoverIcon] = useState<boolean>(false);
  const [sidebarExtended, setSidebarExtended] = useState<boolean>(false);
  const [activeIcon, setActiveIcon] = useState<string>("home");
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [deceptionOpen, setDeceptionOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { glossary, faqs, isAdmin, isDeveloper, hasPlaygroundAccess, isAdvancedUser, state, ...appStateContext } =
    useAppStateContext();

  // Mock deception data - replace with actual data source later
  const deceptionItems: DeceptionItem[] = [
    {
      id: "1",
      technique: "Phishing",
      description:
        "Fraudulent attempts to obtain sensitive information by disguising as trustworthy entities",
      category: "Social Engineering",
    },
    {
      id: "2",
      technique: "Pretexting",
      description:
        "Creating fabricated scenarios to engage victims and steal their information",
      category: "Social Engineering",
    },
  ];

  //obsolete setting
  // const SHOW_SETTINGS_ICON = false; // replaced by role-based rendering below

  const SHOW_ADMIN_ICON = isAdmin || isDeveloper || hasPlaygroundAccess;
  // Advanced (Ninja) icon is enabled only for users with the advanced user role
  const ENABLE_NINJA_ICON = !!isAdvancedUser;

  const accessContext = useContext(AccessContext);
  const { handleLogout } = accessContext;

  const navigate = useNavigate();
  const [logoHovered, setLogoHovered] = useState(false);

  // Function to toggle between light and dark modes
  const toggleMode = () => {
    const newMode = state.themeMode === "light" ? "dark" : "light";
    //store the mode in local storage
    sessionStorage.setItem("systemControlledTheme", "false");
    sessionStorage.setItem("themeMode", newMode);
    // Update the app state with the new mode
    appStateContext.dispatch({ type: "SET_THEME_MODE", payload: newMode });
    // ThemeProvider can't access the state directly, so we use a storage event to notify it of the change
    window.dispatchEvent(new Event("storage"));
  };




  // Filter glossary by search
  const filteredGlossary = (glossary ?? []).filter(
    (item) =>
      item.term.toLowerCase().includes(search.toLowerCase()) ||
      item.definition.toLowerCase().includes(search.toLowerCase()),
  );

  // Filter FAQ by search
  const filteredFaq = (faqs ?? []).filter(
    (item) =>
      item.question.toLowerCase().includes(search.toLowerCase()) ||
      item.answer.toLowerCase().includes(search.toLowerCase()),
  );

  // Filter deception items by search
  const filteredDeception = deceptionItems.filter(
    (item) =>
      item.technique.toLowerCase().includes(search.toLowerCase()) ||
      item.description.toLowerCase().includes(search.toLowerCase()) ||
      item.category?.toLowerCase().includes(search.toLowerCase()),
  );

  const sidebarIconStyle = {
    bgcolor: "transparent",
    borderRadius: 2,
    p: 1.25,
    border: "2px solid #e50c20",
    boxShadow: "0px 2px 4px rgba(229, 12, 32, 0.25)",
    "&:hover": { bgcolor: "rgba(229, 12, 32, 0.1)" },
  };

  const namePieces = accessContext.decodedToken?.name?.split(" ") ?? [];

  return (
    <>
      {/* Left Sidebar */}
      <Stack
        direction="column"
        color={"#000"}
        sx={{
          height: "100%",
          width: "var(--side-bar-width)",
          position: "relative",
          bgcolor: "background.default",
          borderRight: 1,
          borderColor: "divider",
          py: 2,
          px: 1,
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: {
            xs: "2px 0 12px 0 rgba(0,0,0,0.22)", // subtle shadow for mobile
            sm: "4px 0 24px 0 rgba(0,0,0,0.28)", // deeper shadow for desktop/tablet
          },
          flexShrink: 0,
        }}
      >
        {/* Top section with logo and navigation icons */}
        <Stack spacing={4} alignItems="center">
          <IconButton
            onClick={() => {
              setSidebarExtended((curr) => !curr);
              setActiveIcon("home");
              navigate("/");
            }}
            onMouseEnter={() => setLogoHovered(true)}
            onMouseLeave={() => setLogoHovered(false)}
            sx={{
              cursor: "pointer",
              width: 40,
              height: 40,
              alignContent: "center",
              textAlign: "center",
            }}
          >
            <Box
              component="img"
              src={LogoContainer}
              alt="Logo"
              width={40}
              height={40}
              sx={{
                "&:hover": {
                  backgroundColor: "#9a9494",
                  borderRadius: "25%",
                },
                transition: "filter 0.2s",
              }}
            />
          </IconButton>
          <Stack spacing={0.5} alignItems="center">
            {SHOW_HOME_ICON && (
              <IconButton
                onClick={() => {
                  setActiveIcon("home");
                  window.location.href = "/";
                }}
                sx={activeIcon === "home" ? sidebarIconStyle : undefined}
              >
                <Box
                  component="img"
                  src={RegularIcon}
                  alt="Chat"
                  width={20}
                  height={20}
                  sx={
                    activeIcon === "home"
                      ? { filter: "invert(1)" }
                      : { filter: "brightness(0) saturate(100%)" }
                  }
                />
              </IconButton>
            )}

            {SHOW_NAV1_ICON && (
              <IconButton
                onClick={() => setGlossaryOpen(true)}
                sx={activeIcon === "glossary" ? sidebarIconStyle : undefined}
              >
                <BookOpenIcon
                  width={20}
                  height={20}
                  style={{
                    color:
                      activeIcon === "glossary"
                        ? "white"
                        : state.themeMode === "dark"
                          ? "white"
                          : "black",
                  }}
                />
              </IconButton>
            )}

            {SHOW_NAV2_ICON && (
              <IconButton
                onClick={() => setFaqOpen(true)}
                sx={activeIcon === "faq" ? sidebarIconStyle : undefined}
              >
                <QuestionMarkCircleIcon
                  width={20}
                  height={20}
                  style={{
                    color:
                      activeIcon === "faq"
                        ? "white"
                        : state.themeMode === "dark"
                          ? "white"
                          : "black",
                  }}
                />
              </IconButton>
            )}

            {SHOW_NAV3_ICON && (
              <IconButton
                onClick={() => {

                //setActiveIcon("deception");

                  // Specify not to ladd continue negotiation option
                  appStateContext.setShouldAddContinueOption(false);

                  onSidebarInsert("Who may be trying to deceive you?", OrchestrationType.DeceptionAnalysis);
                }}
                sx={  state.currentOrchestrationType === OrchestrationType.DeceptionAnalysis  ? sidebarIconStyle : undefined}
              >
                <FingerPrintIcon
                  width={20}
                  height={20}
                  style={{
                    color:
                    state.themeMode === "dark"
                          ? "white"
                          : "black"
                  }}
                />
              </IconButton>
            )}

            {/* Show Ninja Icon  */}
            {SHOW_NAV4_ICON && (
              <IconButton
                disabled={!ENABLE_NINJA_ICON}
                onClick={() => {

                //setActiveIcon("adhoc_icon");

                  // Specify not to add continue negotiation option
                  appStateContext.setShouldAddContinueOption(false);

                  onSidebarInsert("I want to run an advanced negotiation flow.", OrchestrationType.AdhocNegotiation);
                }}
                sx={{
                  ...(state.currentOrchestrationType === OrchestrationType.AdhocNegotiation ? sidebarIconStyle : {}),
                  ...((!ENABLE_NINJA_ICON) && {
                    opacity: 0.8,
                    cursor: 'not-allowed',
                    '&:hover': {
                      bgcolor: 'transparent'
                    }
                  })
                }}
              >
                <Box
                  component="img"
                  src={NinjaActiveIcon}
                  alt="Ninja"
                  width={20}
                  height={20}
                  sx={{
                    filter: !ENABLE_NINJA_ICON 
                      ? 'grayscale(100%)' 
                      : "none"
                  }}
                />
              </IconButton>
            )}

            {SHOW_ADMIN_ICON && (
              <IconButton
                onClick={() => {
                  setActiveIcon("admin");
                  navigate("/admin"); //"/#/admin";
                }}
                sx={activeIcon === "admin" ? sidebarIconStyle : undefined}
              >
                <Box
                  component="div"
                  width={20}
                  height={20}
                  sx={{
                    display: "grid",
                    color: "text.primary",
                    stroke:
                      state.themeMode === "dark" ? "currentColor" : undefined,
                    ...(activeIcon === "admin" ? { filter: "invert(1)" } : {}),
                  }}
                >
                  <IconRegular4 />
                </Box>
              </IconButton>
            )}
          </Stack>
        </Stack>

        {/* Bottom section with settings and profile */}
        <Stack spacing={2} alignItems="center">
          {isAdmin && (
            <IconButton>
              <Box
                component="img"
                src={RegularIcon5}
                alt="Settings"
                width={24}
                height={24}
              />
            </IconButton>
          )}

          {(SHOW_THEME_ICON || true) && (
            <IconButton
              onClick={() => {
                toggleMode();
              }}
            >
              <Box
                component="img"
                src={state.themeMode === "dark" ? sunIcon : moonIcon}
                alt="Theme"
                width={24}
                height={24}
              />
            </IconButton>
          )}

          {SHOW_AVATAR && (
            <>
              <IconButton onClick={handleMenu}>
                {!profilePhoto ||
                !/^(https:|data:image)/.test(profilePhoto ?? "") ? (
                  /* Display initials */
                  <Avatar alt="Profile" sx={{ width: 32, height: 32 }}>
                    {namePieces[0]?.charAt(0)}
                    {namePieces.length > 1
                      ? namePieces[namePieces.length - 1]?.charAt(0)
                      : ""}
                  </Avatar>
                ) : (
                  <Avatar
                    src={profilePhoto || ""}
                    alt="Profile"
                    sx={{ width: 32, height: 32 }}
                  />
                )}
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    setSubscriptionDialogOpen(true);
                    handleClose();
                  }}
                >
                  Manage Subscription
                </MenuItem>
                <MenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    handleLogout();
                  }}
                >
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>
      </Stack>

      {/* Extended Sidebar */}
      {/* <MuiSidebarExtended open={sidebarExtended} /> */}

      {/* Glossary Drawer */}
      <Drawer
        anchor="left"
        open={glossaryOpen}
        onClose={() => {
          setGlossaryOpen(false);
          setSearch(""); // Reset search when closing popup
        }}
        PaperProps={{
          sx: {
            width: 320,
            left: "var(--side-bar-width)",
            top: 0,
            height: "100vh",
            boxShadow: 3,
          },
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>Glossary</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search glossary..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            margin="dense"
            variant="outlined"
          />
          <List>
            {filteredGlossary.map((item) => (
              <ListItem disablePadding key={item.id}>
                <ListItemButton
                  onClick={() => {

                    //Note: will bring back in future iteration
                    //onSidebarInsert(item.term);
                    setGlossaryOpen(false);
                    setSearch(""); // Reset search when selecting an option
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <ListItemText
                      primary={item.term}
                      secondary={item.definition}
                    />
                    {item.tags && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          color: "text.secondary",
                        }}
                      >
                        Tags: {item.tags}
                      </Typography>
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Drawer>

      {/* FAQ Drawer */}
      <Drawer
        anchor="left"
        open={faqOpen}
        onClose={() => {
          setFaqOpen(false);
          setSearch(""); // Reset search when closing popup
        }}
        PaperProps={{
          sx: {
            width: 400,
            left: "var(--side-bar-width)",
            top: 0,
            height: "100vh",
            boxShadow: 3,
          },
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>FAQ</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search FAQ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            margin="dense"
            variant="outlined"
          />
          <List>
            {filteredFaq.map((item) => (
              <ListItem disablePadding key={item.question}>
                <ListItemButton
                  onClick={() => {
                    
                    //Note: will bring back in future iteration
                    //onSidebarInsert(item.question, );
                    setFaqOpen(false);
                    setSearch(""); // Reset search when selecting an option
                  }}
                >
                  <ListItemText
                    primary={item.question}
                    secondary={item.answer}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Drawer>

      {/* Deception Drawer */}
      <Drawer
        anchor="left"
        open={deceptionOpen}
        onClose={() => {
          setDeceptionOpen(false);
          setSearch(""); // Reset search when closing popup
        }}
        PaperProps={{
          sx: {
            width: 400,
            left: "var(--side-bar-width)",
            top: 0,
            height: "100vh",
            boxShadow: 3,
          },
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>Deception Detection</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search deception techniques..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            margin="dense"
            variant="outlined"
          />
          <List>
            {filteredDeception.map((item) => (
              <ListItem disablePadding key={item.id}>
                <ListItemButton
                  onClick={() => {
                    //Note: will bring back in future iteration
                    //onSidebarInsert(item.technique);
                    setDeceptionOpen(false);
                    setSearch(""); // Reset search when selecting an option
                  }}
                >
                  <ListItemText
                    primary={item.technique}
                    secondary={`${item.description}${item.category ? ` (${item.category})` : ""}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Drawer>

      {/* Subscription Management Dialog */}
      <Dialog
        open={subscriptionDialogOpen}
        onClose={() => setSubscriptionDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>Subscription Management</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <SubscriptionManager />
        </DialogContent>
      </Dialog>
    </>
  );
};
